import { getRepPlayers, getRepItems, getRepItemsActionsFiltered } from '@/store/reports/reports'
import { getSortedCardsIds } from '@/helpers/cards'
import { getHistorySpots } from '@/store/gamestate/gamestate'
import type {
  ReportsLegacyStructure,
  ColValue,
  Res,
  SortConf,
  RepItem,
  SortKey,
  SortAction,
} from '@/types/reports'
import { isReportsLegacyStructure } from '@/types/reports'
import type { State, Getters } from '@/types/store'
import { SNK } from '@/helpers/helpers'
import { namespaces } from '@/helpers/constants'
import type { AggregatedReportResponseSchema } from '@gto/api/vue-query'

function sortByIdx(
  res: RepItem[],
  state: State,
  getters: Getters,
  sortKey: SortKey,
  sortOrderDesc: boolean,
) {
  if (getters.rep_data_street === 'flop') {
    return res.sort((a, b) => (sortOrderDesc ? b.data_idx - a.data_idx : a.data_idx - b.data_idx))
  }
  if (getters.rep_data_street === 'turn') {
    if (state.flopGroup === 'swv_turns') {
      const cardValues = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A']
      const orderColor = ['s', 'h', 'd', 'c']
      if (sortKey === 'data_idx') {
        return res.sort((a, b) => {
          const [valA, clrA] = a.id
          const [valB, clrB] = b.id

          if (valA === valB) {
            return cardValues.indexOf(clrA) - cardValues.indexOf(clrB)
          }
          return cardValues.indexOf(valA) - cardValues.indexOf(valB)
        })
      }
      if (sortKey === 'suits') {
        return res.sort((a, b) => {
          const [valA, clrA] = a.id
          const [valB, clrB] = b.id

          if (clrA === clrB) {
            return cardValues.indexOf(valA) - cardValues.indexOf(valB)
          }
          return orderColor.indexOf(clrA) - orderColor.indexOf(clrB)
        })
      }
    } else {
      return res.sort((a, b) => (sortOrderDesc ? b.data_idx - a.data_idx : a.data_idx - b.data_idx))
    }
  }
  return res
}

function sortByValue(res: RepItem[], sortKey: SortKey, sortOrderDesc: boolean) {
  return res.sort((a, b) =>
    sortOrderDesc ? (b[sortKey] > a[sortKey] ? 1 : -1) : a[sortKey] > b[sortKey] ? 1 : -1,
  )
}

function sortByAction(res: RepItem[], sortAction: SortAction, sortOrderDesc: boolean) {
  if (sortAction.order_reverse != null) {
    return res.sort((a, b) => {
      const freqA = a.solution[sortAction.order_reverse!]?.frequency ?? 0
      const freqB = b.solution[sortAction.order_reverse!]?.frequency ?? 0

      if (freqA !== freqB) {
        return sortOrderDesc ? freqB - freqA : freqA - freqB
      }

      return sortOrderDesc ? b.data_idx - a.data_idx : a.data_idx - b.data_idx
    })
  }
  if (sortAction.group_display_name && sortAction.actions) {
    const aaOrders = sortAction.actions.map(a => a.order_reverse)

    return res.sort((a, b) => {
      const totalFreqA = aaOrders.reduce<number>((sum, order) => {
        if (order !== undefined && order !== null) {
          const freq = a.solution[order]?.frequency ?? 0
          return sum + (freq as number)
        }
        return sum
      }, 0)

      const totalFreqB = aaOrders.reduce<number>((sum, order) => {
        if (order !== undefined && order !== null) {
          const freq = b.solution[order]?.frequency ?? 0
          return sum + (freq as number)
        }
        return sum
      }, 0)

      return sortOrderDesc ? totalFreqB - totalFreqA : totalFreqA - totalFreqB
    })
  }
  return res
}

const getInitialState = () => ({
  updated: 0,
  hSpot: {},

  hero: { relative_postflop_position: '' },
  opponent: { relative_postflop_position: '' },
  solution: [],
  scrolledPercent: 0,

  reportItem: {},
  reportItem_id: null,

  flopGroup: 'swv_flops',

  flop_sortOrder: 'desc',
  flop_sortBy: 'idx',
  flop_sortByIdx: 'data_idx',
  flop_sortByValue: 'ip_ev',
  flop_sortByAction: null,
  flop_sortByTurn: 'suits',

  turn_filterHand: '',

  totals: null,
  filtered_totals: null,
  solved_action_counts: null,
  eveqeqrAxes: null,

  customReportMeta: {
    status: 'not_exist',
    price: -1,
    solved_board_count: undefined,
    total_board_count: undefined,
  },
})

let gw_reportsItems = []

// const state = getInitialState()

const getters = {
  rep_data_scrolledPercent: (state: State) => state.scrolledPercent,
  rep_turn_filterHand: (state: State) => state.turn_filterHand,

  rep_data_updated: (state: State) => state.updated,
  rep_data_hero: (state: State) => state.hero,
  rep_data_opponent: (state: State) => state.opponent,
  rep_data_solution: (state: State) => state.solution,
  rep_data_eveqeqrAxes: (state: State) => state.eveqeqrAxes,
  // rep_data_hSpot: (state) => state.solution,
  rep_data_hSpot: (state: State) => state.hSpot,
  rep_data_street: (state: State) => state.hSpot?.street || 'preflop',
  rep_data_reportItem: (state: State) => state.reportItem,
  rep_data_reportItemId: (state: State) => state.reportItem_id,
  rep_data_flopGroup: (state: State) => state.flopGroup,
  rep_nav_resizeDeps: (getters: Getters) => ({
    windowSize: getters.globals_window_size,
    focusMode: getters.focus_mode,
    hSpotsMinimized: !getters.globals_fullscreen,
    // reportsFullscreen: getters.sw_study_range_fullscreen,
    reportsFullscreen: getters.sw_study_reports_fullscreen,
    studyLayout: getters.sw_layout_study,
  }),
  rep_data_viewProps: (state: State, getters: Getters): Res => {
    const content =
      getters.rep_data_street === 'flop'
        ? getters.sw_reports_flop_content
        : getters.sw_reports_turn_content

    const reportsStreetGrouping =
      getters.rep_data_street === 'flop'
        ? getters.sw_reports_flop_grouping
        : getters.street === 'turn'
          ? getters.sw_reports_turn_grouping
          : ''

    const reportsStreetGroup = reportsStreetGrouping.replace('swv_', '')

    const colValue: ColValue = content.includes('table')
      ? {
          key: 'strategy',
          val: 'strategy',
          showProp: getters.sw_hand_content.replace('swv_', ''),
        }
      : [
          {
            val: getters.sw_rep_strategy,
            key: 'strategy',
            showProp: getters.sw_hand_content.replace('swv_', ''),
          },
          { val: getters.sw_rep_ev, key: 'ev' },
          { val: getters.sw_rep_eq, key: 'eq' },
          { val: getters.sw_rep_eqr, key: 'eqr' },
        ].find(v => v.val !== 'off') ?? {
          key: 'strategy',
          val: 'strategy',
          showProp: 'strategy',
        }

    const res: Res = {
      updated: state.updated,
      content,
      actionsGrouping: getters.sw_actions_grouping,
      filterActions: getters.sol_filter_actions,
      reportsStreetGrouping,
      reportsStreetGroup,
      flop_sortByIdx: state.flop_sortByIdx,
      flop_sortByValue: state.flop_sortByValue,
      flop_sortByAction: state.flop_sortByAction,
      flop_sortByTurn: state.flop_sortByTurn,
      flop_sortBy: state.flop_sortBy,
      flop_sortOrder: state.flop_sortOrder,
      flop_sortOrderDesc: state.flop_sortOrder === 'desc',
      scrollerKey: `${state.flop_sortOrder}_${
        state.flop_sortByAction?.group_display_name ||
        state.flop_sortByAction?.code ||
        state.flop_sortByValue
      }`,
      heroLeft: getters.rep_data_hero.relative_postflop_position === 'OOP',
      sw_rep_strategy: getters.sw_rep_strategy,
      sw_rep_ev: getters.sw_rep_ev,
      sw_rep_eq: getters.sw_rep_eq,
      sw_rep_eqr: getters.sw_rep_eqr,
      colValue,
      heroRelPos: getters.rep_data_hero.relative_postflop_position.toLowerCase(),
      chartSplit: colValue.val === 'hero_opponent',
      display_strategy: colValue.key === 'strategy',
      oop_player: state.hero.relative_postflop_position !== 'IP' ? state.hero : state.opponent,
      ip_player: state.hero.relative_postflop_position === 'IP' ? state.hero : state.opponent,
      display_oop:
        colValue.val === 'hero_opponent' ||
        (colValue.val === 'opponent' && state.opponent.relative_postflop_position === 'OOP') ||
        (colValue.val === 'hero' && state.hero.relative_postflop_position === 'OOP'),
      display_ip:
        colValue.val === 'hero_opponent' ||
        (colValue.val === 'opponent' && state.opponent.relative_postflop_position === 'IP') ||
        (colValue.val === 'hero' && state.hero.relative_postflop_position === 'IP'),
    }

    return res
  },

  rep_data_items:
    (state: State, getters: Getters, rootState: State, rootGetters: Getters) => () => {
      let res: RepItem[] = gw_reportsItems

      const gamestateNamespace = rootGetters.user_has_flag('CR_REPORTS') ? namespaces.REPORTS : ''

      const { filterActions } = getters.rep_data_viewProps
      let available_actions = JSON.parse(
        JSON.stringify(rootGetters[SNK('available_actions', gamestateNamespace)]),
      ).reverse()

      if (filterActions.length) {
        available_actions = available_actions.filter(as =>
          filterActions.some(a => a.code === as.code),
        )
        res = getRepItemsActionsFiltered({ repItems: res, available_actions })
      }

      const sortOrderDesc = state.flop_sortOrder === 'desc'
      const sortKey =
        state.flop_sortBy === 'idx'
          ? state.flop_sortByIdx
          : state.flop_sortBy === 'value'
            ? state.flop_sortByValue
            : state.flop_sortBy === 'action'
              ? state.flop_sortByAction
              : 'data_idx'

      const emptyItems = res.filter(i => i.isEmpty)
      if (state.flopGroup === 'swv_turns') {
        if (sortKey !== 'suits') {
          res = res.filter(i => !i.isEmpty)
        }
      }

      if (state.flop_sortBy === 'idx') {
        res = sortByIdx(res, state, getters, sortKey, sortOrderDesc)
      } else if (state.flop_sortBy === 'value') {
        res = sortByValue(res, sortKey, sortOrderDesc)
      } else if (state.flop_sortBy === 'action' && state.flop_sortByAction) {
        res = sortByAction(res, state.flop_sortByAction, sortOrderDesc)
      }

      if (state.flopGroup === 'swv_turns' && sortKey !== 'suits') {
        res = [...res, ...emptyItems]
      }

      return res
    },
  rep_data_totalsItems: (state: State) => {
    const res = [state.totals, state.filtered_totals].filter(i => i)
    return res
  },
  rep_data_solvedActionCounts: (state: State) => state.solved_action_counts,
  rep_data_report_insufficient_balance: (state: State, rootGetters: Getters) =>
    state.customReportMeta.price > rootGetters.user_info.credit_balance,
  rep_data_customReportMeta: (state: State) => state.customReportMeta,
}

function getTurnTotals({ players, totals }) {
  const ipPlayer = players.find(p => p.relative_position === 'IP')
  const oopPlayer = players.find(p => p.relative_position === 'OOP')
  const res = {
    actions: totals,
    ip_eq: ipPlayer.total_eq,
    ip_eqr: ipPlayer.total_eqr,
    ip_ev: ipPlayer.total_ev,
    oop_eq: oopPlayer.total_eq,
    oop_eqr: oopPlayer.total_eqr,
    oop_ev: oopPlayer.total_ev,
  }
  return res
}

function getHspotFromApisReports({ game, available_actions }) {
  const spot = {
    available_actions,
    game,
  }
  let res = getHistorySpots({
    history_spots: [spot],
  })
  res = res[0]
  res.id = 'rep_spot_dontuseid'
  return res
}

function repDataTransformer(
  data: ReportsLegacyStructure | AggregatedReportResponseSchema,
  isTurn: Boolean,
): ReportsLegacyStructure {
  // Check if the data is already in the original v1 structure format for both flop and turn
  if (isReportsLegacyStructure(data)) {
    // The object is already in the original structure, return it as is
    return data
  }

  // otherwise parse the new v4 structure to the original v1 structure
  let transformedData
  if (!isTurn) {
    const oopPlayer = data.players.find(player => player.relative_position === 'OOP')
    const ipPlayer = data.players.find(player => player.relative_position === 'IP')
    transformedData = {
      data: data.solutions
        .filter(solution => solution.action_solutions && solution.action_solutions.length)
        .map(solution => {
          const oopSolution = solution.player_solutions?.find(
            ps => ps.position === oopPlayer?.position || ps.position === 'OOP',
          )
          const ipSolution = solution.player_solutions?.find(
            ps => ps.position === ipPlayer?.position || ps.position === 'IP',
          )

          return {
            ratio: solution?.ratio || 0,
            oop_ev: oopSolution?.ev || 0,
            oop_eq: oopSolution?.eq || 0,
            ip_ev: ipSolution?.ev || 0,
            ip_eq: ipSolution?.eq || 0,
            oop_eqr: oopSolution?.eqr || 0,
            ip_eqr: ipSolution?.eqr || 0,
            actions: solution.action_solutions?.map(actionSolution => ({
              action_code: actionSolution.action_code,
              frequency: actionSolution.frequency,
            })),
            flop: solution.name, // Assuming solution name represents the flop
            id: solution.name,
          }
        }),
      totals: {
        oop_ev:
          data.player_totals.find(
            pt => pt.position === oopPlayer?.position || pt.position === 'OOP',
          )?.ev || 0,
        oop_eq:
          data.player_totals.find(
            pt => pt.position === oopPlayer?.position || pt.position === 'OOP',
          )?.eq || 0,
        ip_ev:
          data.player_totals.find(pt => pt.position === ipPlayer?.position || pt.position === 'IP')
            ?.ev || 0,
        ip_eq:
          data.player_totals.find(pt => pt.position === ipPlayer?.position || pt.position === 'IP')
            ?.eq || 0,
        oop_eqr:
          data.player_totals.find(
            pt => pt.position === oopPlayer?.position || pt.position === 'OOP',
          )?.eqr || 0,
        ip_eqr:
          data.player_totals.find(pt => pt.position === ipPlayer?.position || pt.position === 'IP')
            ?.eqr || 0,
        actions: data.action_totals.map(actionTotal => ({
          action_code: actionTotal.action_code,
          frequency: actionTotal.frequency,
        })),
        ratio: data.filtered_ratio,
      },
      filtered_totals: {
        oop_ev:
          data.filtered_player_totals?.find(
            pt => pt.position === oopPlayer?.position || pt.position === 'OOP',
          )?.ev || 0,
        oop_eq:
          data.filtered_player_totals?.find(
            pt => pt.position === oopPlayer?.position || pt.position === 'OOP',
          )?.eq || 0,
        ip_ev:
          data.filtered_player_totals?.find(
            pt => pt.position === ipPlayer?.position || pt.position === 'IP',
          )?.ev || 0,
        ip_eq:
          data.filtered_player_totals?.find(
            pt => pt.position === ipPlayer?.position || pt.position === 'IP',
          )?.eq || 0,
        oop_eqr:
          data.filtered_player_totals?.find(
            pt => pt.position === oopPlayer?.position || pt.position === 'OOP',
          )?.eqr || 0,
        ip_eqr:
          data.filtered_player_totals?.find(
            pt => pt.position === ipPlayer?.position || pt.position === 'IP',
          )?.eqr || 0,
        actions:
          data.filtered_action_totals?.map(actionTotal => ({
            action_code: actionTotal.action_code,
            frequency: actionTotal.frequency,
          })) || [],
        ratio: data.filtered_ratio,
      },
      solved_action_counts: {
        solved_action_counts:
          data.filtered_action_totals?.map(actionTotal => actionTotal.solved_action_count) || [],
        solved_action_counts_total:
          data.filtered_action_totals?.reduce((acc, value) => acc + value.solved_action_count, 0) ||
          0,
        solved_board_count: data.solved_board_count || null,
      },
      relative_hero_position: data.players.find(p => p.is_hero)?.relative_position || 'OOP',
      game_point: data.game_point,
      player_solutions: data.players,
    }
  } else {
    const oopPlayer = data.players.find(player => player.relative_position === 'OOP')
    const ipPlayer = data.players.find(player => player.relative_position === 'IP')
    transformedData = {
      solutions: data.solutions.map(solution => ({
        solutions: solution.action_solutions?.reverse(),
        name: solution.name,
      })),
      player_solutions: [
        {
          ...oopPlayer,
          total_ev:
            data.player_totals.find(
              pt => pt.position === oopPlayer?.position || pt.position === 'OOP',
            )?.ev || 0,
          total_eq:
            data.player_totals.find(
              pt => pt.position === oopPlayer?.position || pt.position === 'OOP',
            )?.eq || 0,
          total_eqr:
            data.player_totals.find(
              pt => pt.position === oopPlayer?.position || pt.position === 'OOP',
            )?.eqr || 0,
          solutions: data.solutions.map(solution => ({
            name: solution.name,
            ev: solution.player_solutions?.find(
              pt => pt.position === oopPlayer?.position || pt.position === 'OOP',
            )?.ev,
            eq: solution.player_solutions?.find(
              pt => pt.position === oopPlayer?.position || pt.position === 'OOP',
            )?.eq,
            eqr: solution.player_solutions?.find(
              pt => pt.position === oopPlayer?.position || pt.position === 'OOP',
            )?.eqr,
          })),
        },
        {
          ...ipPlayer,
          total_ev:
            data.player_totals.find(
              pt => pt.position === ipPlayer?.position || pt.position === 'IP',
            )?.ev || 0,
          total_eq:
            data.player_totals.find(
              pt => pt.position === ipPlayer?.position || pt.position === 'IP',
            )?.eq || 0,
          total_eqr:
            data.player_totals.find(
              pt => pt.position === ipPlayer?.position || pt.position === 'IP',
            )?.eqr || 0,
          solutions: data.solutions.map(solution => ({
            name: solution.name,
            ev: solution.player_solutions?.find(
              pt => pt.position === ipPlayer?.position || pt.position === 'IP',
            )?.ev,
            eq: solution.player_solutions?.find(
              pt => pt.position === ipPlayer?.position || pt.position === 'IP',
            )?.eq,
            eqr: solution.player_solutions?.find(
              pt => pt.position === ipPlayer?.position || pt.position === 'IP',
            )?.eqr,
          })),
        },
      ],
      game_point: data.game_point,
      totals: data.action_totals.reverse(),
    }
  }

  return transformedData
}

const actions = {
  rep_data_initialize({ state, getters, rootGetters, commit }, result) {
    if (result?.response?.status !== 200 || !result.response?.data) {
      return
    }

    const gamePoint = result.response.data?.game_point || result.response.data.results.game_point
    const hSpot = getHspotFromApisReports(gamePoint)
    const { street } = hSpot
    const isTurn = street === 'turn'

    // BE returns QT7 on flop always
    if (hSpot.street === 'flop') {
      hSpot.board = getters.flop_board || hSpot.board
    }

    const apiData: ReportsLegacyStructure = repDataTransformer(result.response.data, isTurn)

    // console.log('hSpot', hSpot)

    const available_actions = JSON.parse(JSON.stringify(hSpot.available_actions)).reverse()

    // console.log(apiData, 'apiData test')

    if (isTurn && !apiData.totals) return
    if (!isTurn && !(apiData.totals || apiData.results?.totals)) return

    const totals = isTurn
      ? getTurnTotals({
          players: apiData?.player_solutions,
          totals: apiData.totals,
        })
      : apiData.totals || apiData.results?.totals

    const filtered_totals = isTurn
      ? null
      : apiData.filtered_totals || apiData.results?.filtered_totals

    const solved_action_counts =
      apiData.solved_action_counts || apiData.results?.solved_action_counts

    const relativeHeroPosition = (
      apiData?.relative_hero_position || apiData?.results?.relative_hero_position
    )?.toLowerCase()
    const players = isTurn
      ? apiData?.player_solutions.map(p => ({
          ...p,
          relative_postflop_position: p.relative_position,
        }))
      : hSpot.players

    const activeTotals = isTurn
      ? totals
      : JSON.stringify(totals) !== JSON.stringify(filtered_totals)
        ? filtered_totals
        : totals

    const { hero, opponent, ipPlayer, oopPlayer } = getRepPlayers({
      players,
      isTurn,
      totals: activeTotals,
      relativeHeroPosition,
      hSpot,
    })

    const solution = available_actions.map(a => {
      const frequency = activeTotals.actions?.[a.order]?.frequency
      const r = {
        ...a,
        frequency,
      }
      return r
    })

    if (rootGetters.user_has_flag('CR_REPORTS')) {
      let hSpots = rootGetters[SNK('history_spots', namespaces.REPORTS)]
      hSpots = hSpots.map(hs => {
        if (hs.is_active) {
          hs.available_actions = hs.available_actions.map(aa => {
            const r = solution.find(a => aa.code === a.code)
            return {
              ...aa,
              ...r,
              is_selected: aa.is_selected,
            }
          })
        }
        return hs
      })
      commit(
        SNK('gamestate_set', namespaces.REPORTS),
        {
          key: 'history_spots',
          value: hSpots,
        },
        { root: true },
      )
    }

    const isGroup =
      Array.isArray(apiData.data) &&
      apiData.data.some(obj => Object.prototype.hasOwnProperty.call(obj, 'group'))

    let repApiData = isTurn
      ? apiData.solutions
      : isGroup
        ? apiData.data
        : apiData?.results?.data || apiData.data

    const idKey = isTurn ? 'name' : isGroup ? 'group' : 'flop'

    if (isTurn) {
      repApiData = repApiData.map((i, idx) => {
        const r = {
          ...i,
          ip_ev: ipPlayer.solutions[idx]?.ev,
          ip_eq: ipPlayer.solutions[idx]?.eq,
          ip_eqr: ipPlayer.solutions[idx]?.eqr,
          oop_ev: oopPlayer.solutions[idx]?.ev,
          oop_eq: oopPlayer.solutions[idx]?.eq,
          oop_eqr: oopPlayer.solutions[idx]?.eqr,
        }
        return r
      })
    }

    const flopGroup =
      street === 'flop' ? getters.sw_reports_flop_grouping : getters.sw_reports_turn_grouping

    let { items, eveqeqrAxes } = getRepItems({
      repApiData,
      totals,
      // isTurn,
      available_actions,
      idKey,
      ipPlayer,
      oopPlayer,
      flopGroup,
    })

    if (isTurn && flopGroup === 'swv_turns') {
      // debugger
      const allCards = getSortedCardsIds()
      items = allCards.map((c, idx) => {
        const r = {
          ...(items.find(cc => cc.id === c) || {
            id: c,
            isEmpty: true,
            ip: { ev: 0, eq: 0, eqr: 0 },
            oop: { ev: 0, eq: 0, eqr: 0 },
            backgrounds: {},
            actions: [],
            solution: [],
          }),
          data_idx: idx,
          flopGroup,
        }
        return r
      })
    }

    const { items: totalItems } = getRepItems({
      repApiData: [totals],
      totals,
      // isTurn,
      available_actions,
      idKey,
      flopGroup,
      ipPlayer,
      oopPlayer,
    })

    let filteredTotalItems = null
    if (!isTurn) {
      const { items: l_filteredTotalItems } = getRepItems({
        repApiData: [filtered_totals],
        totals,
        available_actions,
        idKey,
        flopGroup,
        ipPlayer,
        oopPlayer,
      })
      filteredTotalItems = l_filteredTotalItems
    }

    // fallback or keep sorting
    let sortConf: SortConf | null = null
    const sortConfString = localStorage.getItem('rep_sortConf')
    if (sortConfString) {
      try {
        sortConf = JSON.parse(sortConfString) as SortConf
        if (sortConf) {
          if (sortConf.sortBy === 'action') {
            const repDataSortAction = solution.find(a => a.code === sortConf!.sortByAction?.code)
            if (!repDataSortAction) {
              sortConf.sortBy = 'idx'
              sortConf.sortByIdx = 'data_idx'
            } else {
              sortConf.sortByAction = repDataSortAction
            }
          }
          state.flop_sortOrder = sortConf.sorOrder
          state.flop_sortBy = sortConf.sortBy
          state.flop_sortByIdx = sortConf.sortByIdx
          state.flop_sortByValue = sortConf.sortByValue
          state.flop_sortByAction = sortConf.sortByAction

          localStorage.setItem('rep_sortConf', JSON.stringify(sortConf))
        }
      } catch (e) {
        // Handle parsing error
      }
    }

    state.totals = totalItems[0]
    state.filtered_totals = isTurn
      ? null
      : filteredTotalItems &&
          typeof filteredTotalItems[0] === 'object' &&
          filteredTotalItems[0] !== null
        ? {
            ...(filteredTotalItems[0] as object),
            ratio: filtered_totals?.ratio,
          }
        : null

    state.hero = hero
    state.opponent = opponent
    state.solution = solution
    state.reportItem = items.find(i => !i.isEmpty && i.id === state.reportItem?.id) || items[0]
    state.reportItem_id = ''
    state.eveqeqrAxes = eveqeqrAxes
    // state.flop_isGroup = isGroup
    state.flopGroup = flopGroup // so table head is updated afterward

    gw_reportsItems = items
    state.hSpot = hSpot
    state.updated = Date.now()

    if (
      rootGetters.user_has_flag('CR_REPORTS') &&
      solved_action_counts?.solved_action_counts?.length > 0 &&
      solved_action_counts?.solved_board_count
    ) {
      // if all solved_action_counts are the same as solved board count we don't want to show the "used by" row
      const allMatch = solved_action_counts.solved_action_counts.every(
        count => count === solved_action_counts.solved_board_count,
      )
      state.solved_action_counts = allMatch ? null : solved_action_counts
    }
  },
  rep_persistSortConf({ state }) {
    const rep_sortConf = {
      sorOrder: state.flop_sortOrder,
      sortBy: state.flop_sortBy,
      sortByIdx: state.flop_sortByIdx,
      sortByValue: state.flop_sortByValue,
      sortByAction: state.flop_sortByAction,
    }
    // console.log(rep_sortConf)

    // test wrong sort conf
    // rep_sortConf.sortBy = 'idx'
    // rep_sortConf.sortBy = 'value'
    // rep_sortConf.sortBy = 'nonsence'
    // rep_sortConf.sortBy = 'action'
    // rep_sortConf.flop_sortByIdx = 'nonsence'
    // rep_sortConf.flop_sortByValue = 'nonsence'
    // rep_sortConf.sortByAction = null

    localStorage.setItem('rep_sortConf', JSON.stringify(rep_sortConf))
    // console.log('rep_sortConf set', rep_sortConf)
  },
}

const mutations = {
  reports_reset(state, initialState) {
    Object.assign(state, initialState || getInitialState())
    gw_reportsItems = []
  },
  reports_set(state: State, { key, value }) {
    state[key] = value
  },
}

export default {
  state: getInitialState(),
  getters,
  actions,
  mutations,
}
